import React, { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { FiKey, FiMail, FiArrowRight, FiAlertCircle } from 'react-icons/fi';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface PasskeyLoginProps {
  onSuccess?: () => void;
  onFallback?: () => void;
}

const PasskeyLogin: React.FC<PasskeyLoginProps> = ({ onSuccess, onFallback }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const handlePasskeyLogin = async (userEmail?: string) => {
    if (!window.PublicKeyCredential) {
      toast.error('Your browser doesn\'t support passkeys. Please use a modern browser.');
      return;
    }

    setIsAuthenticating(true);

    try {
      // Get authentication options from server
      const optionsResponse = await api.getWebAuthnLoginOptions(userEmail);
      const options = optionsResponse.data;

      // Start authentication with browser
      let attResp;
      try {
        attResp = await startAuthentication({ optionsJSON: options });
      } catch (error: any) {
        if (error.name === 'NotAllowedError') {
          toast.error('Authentication was cancelled or timed out. Please try again.');
        } else if (error.name === 'InvalidStateError') {
          toast.error('No passkey found for this device. Please sign in with password.');
          if (onFallback) onFallback();
        } else {
          toast.error('Failed to authenticate. Please try again.');
        }
        setIsAuthenticating(false);
        return;
      }

      // Verify authentication with server
      const verificationResponse = await api.verifyWebAuthnLogin({
        ...attResp,
        challenge: options.challenge
      });

      if (verificationResponse.data.verified) {
        // Store token if provided
        if (verificationResponse.data.token) {
          localStorage.setItem('authToken', verificationResponse.data.token);
        }

        toast.success('Signed in successfully!');
        
        // Refresh user data
        await refreshUser();
        
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/dashboard');
        }
      } else {
        toast.error('Authentication failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Passkey authentication error:', error);
      
      if (error.response?.status === 404) {
        toast.error('No passkey found. Please sign in with password or register a passkey.');
        if (onFallback) onFallback();
      } else {
        toast.error(error.response?.data?.error || 'Failed to authenticate');
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    handlePasskeyLogin(email);
  };

  return (
    <div className="space-y-4">
      {/* Quick Sign In (Discoverable Credentials) */}
      {!showEmailInput && (
        <>
          <button
            onClick={() => handlePasskeyLogin()}
            disabled={isAuthenticating}
            className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isAuthenticating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Authenticating...
              </>
            ) : (
              <>
                <FiKey className="w-5 h-5 mr-2" />
                Sign in with Passkey
              </>
            )}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowEmailInput(true)}
            className="w-full py-2 px-4 text-gray-600 hover:text-gray-700 text-sm"
          >
            Sign in with email + passkey
          </button>
        </>
      )}

      {/* Email + Passkey Sign In */}
      {showEmailInput && (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                required
                autoFocus
              />
              <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>

          <button
            type="submit"
            disabled={isAuthenticating}
            className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isAuthenticating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Authenticating...
              </>
            ) : (
              <>
                Continue with Passkey
                <FiArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setShowEmailInput(false)}
            className="w-full text-sm text-gray-600 hover:text-gray-700"
          >
            Back to quick sign in
          </button>
        </form>
      )}

      {/* Fallback Option */}
      {onFallback && (
        <button
          type="button"
          onClick={onFallback}
          className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Sign in with password instead
        </button>
      )}

      {/* Help Text */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-start">
          <FiAlertCircle className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-600">
            <p className="font-medium mb-1">How passkey sign-in works:</p>
            <ul className="space-y-0.5">
              <li>• Use Face ID, Touch ID, or Windows Hello</li>
              <li>• Insert your YubiKey when prompted</li>
              <li>• Use your password manager (1Password, etc.)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasskeyLogin;